import dayjs from 'dayjs'
import { OrdineType, PersonalizzazioneOrdineType, PosizionePersonalizzazioneCarrelloStatusType, UserType, UtenteType } from './types'
import { StatoOrdineStatus } from '@components/atoms/StatoOrdine/StatoOrdine'

export const cn = (...classes: Array<string | false | null | undefined>) => {
  return classes.filter(Boolean).join(' ')
}

export const dateFormat = (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : undefined
export const timeFormat = (date: string) => date ? dayjs(date).format('HH:mm') : undefined

export const dateTimeFormat = (date: string) => dayjs(date).format('DD/MM/YYYY - HH:mm')

export const dateChatFormat = (date: string | number | Date | null, t: any) => {
  if (!date) return ''

  const parsed = dayjs(date)
  if (!parsed.isValid()) return ''

  const now = dayjs()
  const diffDays = now.diff(parsed, 'day', true)
  if (parsed.isBefore(now) && diffDays <= 5) {
    const wholeDays = Math.floor(diffDays)
    return t(`{0} giorni fa - {1}`, [wholeDays, parsed.format('HH:mm')])
  }

  return parsed.format('DD/MM/YYYY - HH:mm')
}

export const priceValutaNotation = (value: number | null | undefined, floatingDigits = 3) => {
  if (value === null || value === undefined || Number.isNaN(value)) return ''

  return `${new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: floatingDigits,
    maximumFractionDigits: floatingDigits,
    useGrouping: true,
  }).format(value)} €`
}


export const isOutsideClick = (e, ref) => {
  var rect = ref.current?.getBoundingClientRect()
  // Se clientX e clientY sono a 0, è un input da tastiera
  if (!e.clientX && !e.clientY) return false
  return !(
    rect.top <= e.clientY &&
    e.clientY <= rect.top + rect.height &&
    rect.left <= e.clientX &&
    e.clientX <= rect.left + rect.width
  )
}

export const getAssetsUrl = (url) => `/${url}`

export const getImageUrl = (url) => getAssetsUrl(`images/${url}`)

export const toAltTitle = (term: string) => term // Solo per centralizzare nel caso dovessimo prevedere delle regole

const toCamelKey = (key: string) => {
  if (!key.includes('_')) return key
  const segments = key.split('_').filter(Boolean)
  if (segments.length === 0) return key
  const [first, ...rest] = segments
  return first.toLowerCase() + rest.map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()).join('')
}

const toSnakeKey = (key: string) => {
  let result = ''
  let prevWasLowerOrDigit = false
  let prevWasUpper = false

  for (let i = 0; i < key.length; i += 1) {
    const char = key[i]
    const code = char.charCodeAt(0)
    const isUpper = code >= 65 && code <= 90
    const isLower = code >= 97 && code <= 122
    const isDigit = code >= 48 && code <= 57

    if (char === '_' || char === '-' || char === ' ') {
      if (result[result.length - 1] !== '_') result += '_'
      prevWasLowerOrDigit = false
      prevWasUpper = false
      continue
    }

    if (isUpper) {
      const nextCode = key.charCodeAt(i + 1)
      const nextIsLower = !Number.isNaN(nextCode) && nextCode >= 97 && nextCode <= 122
      if (result && result[result.length - 1] !== '_' && (prevWasLowerOrDigit || (prevWasUpper && nextIsLower))) {
        result += '_'
      }
      result += char.toLowerCase()
      prevWasLowerOrDigit = false
      prevWasUpper = true
      continue
    }

    result += char
    prevWasLowerOrDigit = isLower || isDigit
    prevWasUpper = false
  }

  return result
}

export const toCamelCase = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(toCamelCase)
  }
  if (Object.prototype.toString.call(value) === '[object Object]') {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const camelKey = toCamelKey(key)
      result[camelKey] = toCamelCase(val)
    }
    return result
  }
  return value
}

export const toSnakeCase = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(toSnakeCase)
  }
  if (Object.prototype.toString.call(value) === '[object Object]') {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const snakeKey = toSnakeKey(key)
      result[snakeKey] = toSnakeCase(val)
    }
    return result
  }
  return value
}

export const getBozzaMinStatus = (statuses: PosizionePersonalizzazioneCarrelloStatusType[]): PosizionePersonalizzazioneCarrelloStatusType => {
  const statusOrder: PosizionePersonalizzazioneCarrelloStatusType[] = [
    'bozza da caricare', 
    'bozza inviata', 
    'bozza approvata', 
    'bozza rifiutata'
  ]
  statusOrder.forEach(s => {
    if (statuses.includes(s))
      return s
  })
  return null
}

export const getPersonalizzazioneMinStatus = (personalizzazione: PersonalizzazioneOrdineType): PosizionePersonalizzazioneCarrelloStatusType => {
  return getBozzaMinStatus(personalizzazione.posizioni?.map(ps => ps.status).filter(Boolean) || [])
}

export const getStatoOrdineStatus = (ordine: OrdineType): StatoOrdineStatus => {
  // TODO - logiche per determinare lo status (per ora solo simulate)
  if (ordine.dataSpedizione)
    return 'ordine evaso'
  if (!ordine.personalizzazioni?.length)
    return 'ordine in lavorazione'
  const personalizzazioniMinStatuses = ordine.personalizzazioni.map(p => getPersonalizzazioneMinStatus(p)) || []
  if (!!personalizzazioniMinStatuses?.filter(s => !s)?.length)
    return 'ordine in lavorazione'
  return getBozzaMinStatus(personalizzazioniMinStatuses.filter(s => !!s))
}

export const getUserType = (utente: UtenteType): UserType => 'admin' // TODO - logiche per determinare il tipo di utente

export const sortCols = (
  cols: any[], 
  sort: { key: string, direction: 'asc' | 'desc'}[], 
  a: any, 
  b: any
) => {
  let res = 0  // se è tutto uguale lascio in ordine di default
  
  sort.forEach((s) => {
    const col = cols.filter(c => c.key === s.key)?.[0]
    if (!col)
      return

    const sortValueA = col.getSortValue(a)
    const sortValueB = col.getSortValue(b)

    if (sortValueA < sortValueB){
      res = s.direction === 'desc' ? 1 : -1
    }
    if (sortValueA > sortValueB){
      res = s.direction === 'desc' ? -1 : 1
    }
  })
  return res
}

export const swiperA11yMessages = (t) => ({
  prevSlideMessage: t('Slide precedente'),
  nextSlideMessage: t('Prossima slide'),
  firstSlideMessage: t('Prima slide'),
  lastSlideMessage: t('Ultima slide'),
})


export const getCookie = (key: string) => document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)")?.pop() || null

export const setCookie = (key: string, value: string) => {
  if (getCookie(key) === value) return
  document.cookie = `${key}=${value}; path=/`
}
